<template>
  <aha-modal
    id="doc-to-quiz__result-modal"
    :size="modalSize"
    :title="modalTitle"
    :iconBeforeModalTitle="iconBeforeModalTitle"
    boxShadow="0 0 1px 8px rgba(255,255,255,0.3)"
    @hide="handleCloseModal()"
    noCloseOnBackdrop
    :classesModal="{'modal__hidden': isModalHidden, 'doc-to-quiz__result-modal': true}"
  >
    <div class="doc-to-quiz__header" id="doc-to-quiz__header"></div>
    <div class="doc-to-quiz__wrapper" id="doc-to-quiz__content-wrapper">
      <div v-for="(quiz, index) in docToQuizResult" :key="index">
        <AIGeneratedPickAnswerContent
          v-if="quiz.type === AIResponseType.PICK_ANSWER"
          :response="quiz"
          :index="index"
          @handleAddSlideToPresentation="handleAddSlideToPresentation"
          class="content-item"
          :class="{'is-first-content-item': index === 0}"
        />
        <AIGeneratedTypeAnswerContent
          v-if="quiz.type === AIResponseType.TYPE_ANSWER"
          :response="quiz"
          :index="index"
          @handleAddSlideToPresentation="handleAddSlideToPresentation"
          class="content-item"
        />
      </div>
      <aha-spinner
        v-if="!isFeedbackButtonsVisible && !isSubmittingFeedback"
        class="mb-4"
        :class="{'hidden': isLoadingSpinnerHidden}"
      ></aha-spinner>
      <div
        v-if="isResponseWithError"
        class="text-center mt-15 rounded"
        :class="{'bg-base-0 m-4': !customAIStumpedText, 'mb-8': customAIStumpedText}"
      >
        <p v-if="uploadedFileError" class="w-80">
          {{ uploadedFileError }}
        </p>
        <AIStumped v-else :customText="customAIStumpedText" />
      </div>
      <div class="feedback-loading" v-if="isSubmittingFeedback">
        <div class="feedback-loading__content">
          <div class="text-base-100">
            {{ $i18nPresenter.t("Just a moment! Generating your quiz...") }}
          </div>
          <FakeProgressbar :resetProgressBar="resetProgressBar" />
        </div>
      </div>
    </div>
    <div class="doc-to-quiz__bottom"></div>
    <AIFeedbackButtons
      @handleFeedbackQuiz="handleFeedbackQuiz"
      :isFeedbackButtonsVisible="isFeedbackButtonsVisible"
      :allFeedbackButtonTexts="allFeedbackButtonTexts"
      isDocToQuizModal
    />
  </aha-modal>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import EventBus from '@/eventBus.js'
import AIGeneratedPickAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedPickAnswerContent.vue'
import AIGeneratedTypeAnswerContent from '@/components-v2/presentation-editor/new-slide/OpenAI/AIGeneratedTypeAnswerContent.vue'
import AIFeedbackButtons from '../openAI/AIFeedbackButtons.vue'
import FakeProgressbar from '@/components-v2/presentation-editor/new-slide/FakeProgressbar.vue'
import AIStumped from '@/components-v2/presentation-editor/new-slide/OpenAI/AIStumped.vue'
import AIResponseType from '@/constant/aiResponseType'

import utils from '@/util'
import importStatusConstant from '@/constant/importStatus'
import SlideApi from '@/api/slide'
import AIResponseApi from '@/api/aiResponse'
import PresentationUtils from '@/utils/presentation'
import { IMPORT_DOCUMENT } from '@/constant/eventActions'
import { EVENT_CATEGORIES_BY_ROUTE_NAME } from '@/constant/routes'
import openAIConstants from '@/constant/openAI'

// trackers
import SlideMixpanelTracker from '@/utils/mixpanel/slide.mixpanel-tracker'

export default {
  components: {
    AIGeneratedPickAnswerContent,
    AIGeneratedTypeAnswerContent,
    AIStumped,
    FakeProgressbar,
    AIFeedbackButtons,
  },
  // mixins: [UndoRedoMixin],
  data() {
    return {
      importStatusConstant,
      currentContentLanguage: '',
      allFeedbackButtonTexts: openAIConstants.docToQuizFeedbacks,
      isFeedbackButtonsVisible: false,
      isSubmittingFeedback: false,
      AIResponseType,
      isResponseWithError: false,
      uploadedFileError: '',
      resetProgressBar: 0,
      isModalVisible: false,
      eventBusErrorName: 'doc-to-quiz: generating_error',
      iconBeforeModalTitle: {
        name: 'sparkles',
        width: 20,
        height: 20,
        viewBox: '0 0 20 20 ',
        maintainColor: true,
      },
      customAIStumpedText: [],
      isLoadingSpinnerHidden: false,
      numberOfGeneratedSlides: 0,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isNewPresentation: (state) => state.isNewPresentation,
      slideActive: (state) => state.slideActive,
      docToQuizUniqueRequestId: (state) => state.docToQuizUniqueRequestId,
      docToQuizResult: (state) => state.docToQuizResult,
      isGenerateFromDocModalVisible: (state) => state.isGenerateFromDocModalVisible,
      generateFromDocImportStatus: (state) => state.generateFromDocImportStatus,
    }),
    ...mapGetters(['currentPresenterLanguage']),

    isModalHidden() {
      return !this.isModalVisible || !this.docToQuizResult.length
    },
    modalSize() {
      return this.isFrenchOrVietnameseUser ? 'lg' : 'md'
    },
    modalTitle() {
      return this.$i18nPresenter.t('AI-Generated Slides')
    },
    isPreviousQuizzesHaveIds() {
      return this.docToQuizResult.some((quiz) => quiz.id)
    },
    isFrenchOrVietnameseUser() {
      return this.currentPresenterLanguage === 'fr' || this.currentPresenterLanguage === 'vi'
    },
    isGeneratingDocToQuiz() {
      return this.generateFromDocImportStatus !== this.importStatusConstant.NO_IMPORT
    },
  },
  async mounted() {
    EventBus.$on('doc-to-quiz: generated', (socketData) => {
      if (socketData.error) {
        this.handleReceiveNewResponseError(socketData)
        return
      }
      const generatedPresentationId = this.getGeneratedPresentationId(socketData.requestId)
      if (socketData.requestId === this.docToQuizUniqueRequestId && this.presentation.id === generatedPresentationId) {
        this.handleReceiveNewResponses(socketData)
      }
    })
    EventBus.$on('doc-to-quiz: generation_done', () => {
      if (!this.presentation || !this.presentation.id || !this.docToQuizUniqueRequestId) return
      this.isFeedbackButtonsVisible = true
      if (this.isGeneratingDocToQuiz) this.setGenerateFromDocImportStatusAction(this.importStatusConstant.NO_IMPORT)
      if (this.isGenerateFromDocModalVisible) this.setGenerateFromDocModalStateAction(false)
      this.isModalVisible = true
      EventBus.$emit('doc-to-quiz: close_generating_toast')
    })
  },
  destroyed() {
    EventBus.$off('doc-to-quiz: generated')
    EventBus.$off('doc-to-quiz: generation_done')
  },
  methods: {
    ...mapActions([
      'setSlideActiveAction',
      'setDocToQuizUniqueRequestIdAction',
      'setDocToQuizResultAction',
      'setGenerateFromDocModalStateAction',
      'setGenerateFromDocImportStatusAction',
      'setDocToQuizResultModalVisibleAction',
    ]),
    ...mapActions('slidesGeneratorFeedback', ['sendFeedbackOnDocumentQuizzes']),
    getGeneratedPresentationId(requestId) {
      return Number(requestId.split('-')[1])
    },
    handleReceiveNewResponseError(socketData) {
      if (socketData.error && !socketData.message) {
        this.isFeedbackButtonsVisible = true
        this.isResponseWithError = true
        if (!this.docToQuizResult.length) {
          EventBus.$emit(this.eventBusErrorName, 'AI stumped')
        }
        return
      }
      if (socketData.error && socketData.message) {
        this.isFeedbackButtonsVisible = false
        this.isResponseWithError = true
        if (!this.docToQuizResult.length) {
          EventBus.$emit(this.eventBusErrorName, 'AI stumped')
        }
        this.uploadedFileError = socketData.message
      }
    },
    handleReceiveNewResponses(socketData) {
      if (!this.isFeedbackButtonsVisible) {
        this.isFeedbackButtonsVisible = false
        this.isSubmittingFeedback = false
        this.isResponseWithError = false
      }
      const hasResponses = socketData.responses && socketData.responses.type
      if (!hasResponses) return

      this.setDocToQuizResultAction([...this.docToQuizResult, socketData.responses])
      if (!this.currentContentLanguage) this.currentContentLanguage = socketData.language
      this._trackFirstAIResultDisplayed()
      this.numberOfGeneratedSlides += 1
      if (this.docToQuizResult.length === 1) {
        this.setGenerateFromDocImportStatusAction(this.importStatusConstant.NO_IMPORT)
        this.setGenerateFromDocModalStateAction(false)
        this.isModalVisible = true
        EventBus.$emit('doc-to-quiz: close_generating_toast')
      }
    },

    async _scrollToBottom() {
      this.resetProgressBar += 1
      await utils.sleep(500)
      const element = document.getElementById('doc-to-quiz__content-wrapper')
      if (!element) return
      element.scrollTo({
        top: element.scrollHeight + 200,
        behavior: 'smooth',
      })
    },

    async handleFeedbackQuiz(feedback) {
      try {
        this.isSubmittingFeedback = true
        this.isFeedbackButtonsVisible = false
        this._scrollToBottom()
        const requestId = `${utils.generateRandomString()}-${this.presentation.id}`
        this.setDocToQuizUniqueRequestIdAction(requestId)
        const { aiRequestId } = this.docToQuizResult[0]
        const allQuizzes = this.docToQuizResult.map((quiz) => {
          return quiz.data[0].questionTitle
        })
        await this.sendFeedbackOnDocumentQuizzes({
          feedbackMessage: feedback,
          uniqueAccessCode: this.presentation.uniqueAccessCode,
          requestId,
          aiRequestId,
          allQuizzes,
          language: this.currentContentLanguage,
        })
      } catch (error) {
        this.isSubmittingFeedback = false
        this.isResponseWithError = true
        this.isLoadingSpinnerHidden = true
        this.customAIStumpedText = ["Can't generate quiz at the moment."]
        EventBus.$emit(this.eventBusErrorName, 'Error while sending feedback')
        this.setGenerateFromDocModalStateAction(false)
        this.setGenerateFromDocImportStatusAction(this.importStatusConstant.NO_IMPORT)
      }
    },

    _setButtonState(responseId, value, stateKey) {
      const quizzes = this.docToQuizResult.map((res) => {
        return {
          ...res,
          [stateKey]: responseId === res.id ? value : res[stateKey],
        }
      })
      this.setDocToQuizResultAction(quizzes)
    },

    _setPayloadAddSlide(aiResponseId) {
      const payload = {
        aiResponseId,
        presentationId: this.presentation.id,
        textColour: this.slideActive.textColour || '#313131',
        baseColour: this.slideActive.baseColour || '#ffffff',
        backgroundImage: this.slideActive.backgroundImage,
        visibility: this.slideActive.visibility,
      }

      // set payload.order for new slide
      const [newSlideIndex, activeIndex] = this._setPayloadInsertAfter(payload)
      return [payload, newSlideIndex, activeIndex]
    },

    _setPayloadInsertAfter(payload) {
      const activeIndex = this.$store.state.presentation.slides.findIndex((s) => {
        if (s.id) {
          return s.id === this.$store.state.slideActive.id
        }
        return s.timestamp === this.$store.state.slideActive.timestamp
      })
      let newSlideIndex = activeIndex
      if (activeIndex > -1 && !this.isNewPresentation) {
        const isNextSlideLeaderboard = this.presentation.slides[activeIndex + 1] && this.presentation.slides[activeIndex + 1].type === 'leaderboard'
        const activeSlideId = this.presentation.slides[activeIndex] &&
          this.presentation.slides[activeIndex].id

        if (!activeSlideId) {
          payload.insertAfter = this.presentation.slides[activeIndex - 1].order
        }
        if (activeSlideId && !isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order
          newSlideIndex = activeIndex + 1
        }
        if (activeSlideId && isNextSlideLeaderboard) {
          payload.insertAfter = this.presentation.slides[activeIndex].order + 1
          newSlideIndex = activeIndex + 2
        }
      }
      return [newSlideIndex, activeIndex]
    },

    _handleSuggestPresentationTitle(newSlide) {
      PresentationUtils.handleSuggestPresentationTitle(newSlide.title)
    },

    _handleAfterAddSlide(newSlide, newSlideIndex, activeIndex) {
      const isTypeAnswerSlide = newSlide.type === this.AIResponseType.PICK_ANSWER &&
        newSlide.slideType === this.AIResponseType.TYPE_ANSWER

      if (isTypeAnswerSlide) {
        newSlide.otherCorrectQuiz = JSON.parse(newSlide.otherCorrectQuiz)
      }
      const defaultSlideId = this.presentation.slides[activeIndex].id
      this.$store.state.presentation.slides[newSlideIndex] = newSlide
      this.setSlideActiveAction(newSlide)
      EventBus.$emit(`update-right-column-for-slide-title-${this.slideActive.id}`, newSlide.title)
      if (PresentationUtils.needSuggestPresentationTitle()) {
        this._handleSuggestPresentationTitle(newSlide)
      }
      setTimeout(() => {
        EventBus.$emit('SideLeft: setSelectedSlideIds', [newSlide.id])
        utils.showToastSuccess(this.$i18nPresenter.t('Slide added successfully'))
      }, 250)
      if (this.isNewPresentation) {
        this.$store.state.isNewPresentation = false
        // Delete default slide: poll
        if (defaultSlideId) {
          SlideApi.updateSlideById(defaultSlideId, {
            deleted: true,
          })
        }
      }
      if (newSlide.type === this.AIResponseType.PICK_ANSWER) {
        EventBus.$emit('addSlideLeaderboard')
      }
    },

    _trackDocToQuizCreateNewSlide(slideType, slideId) {
      const slideTracker = new SlideMixpanelTracker(this.presentation, this.$route.name)
      slideTracker.setCustomProps({
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
        slideType,
        slideId,
      })
      slideTracker.trackCreateNewSlide('From PPT/PDF to quiz', null, true) // useGA4Tracking = true
    },

    async handleAddSlideToPresentation(response) {
      if (response.isAddingSlide || response.isDisabled) return
      this._setButtonState(response.id, true, 'isAddingSlide')
      try {
        const [payload, newSlideIndex, activeIndex] = this._setPayloadAddSlide(
          response.id,
        )
        const res = await AIResponseApi.createSlide(response.id, payload)
        this._trackDocToQuizCreateNewSlide(response.type, res.body.newSlide.id)
        this._handleAfterAddSlide(res.body.newSlide, newSlideIndex, activeIndex)
      } catch (error) {
        utils.showToastError(this.$i18nPresenter.t('Cannot add this slide to current presentation. Please try again'))
        Sentry.captureException(error)
      } finally {
        this._setButtonState(response.id, false, 'isAddingSlide')
        this._setButtonState(response.id, true, 'isDisabled')
      }
    },

    handleCloseModal() {
      EventBus.$emit('doc-to-quiz: close_generating_toast')
      this.setDocToQuizUniqueRequestIdAction(null)
      this.setGenerateFromDocImportStatusAction(this.importStatusConstant.NO_IMPORT)
      this.setDocToQuizResultAction([])
      this.setDocToQuizResultModalVisibleAction(false)
    },

    _trackFirstAIResultDisplayed() {
      if (this.numberOfGeneratedSlides > 0) {
        return
      }
      const eventAction = `${IMPORT_DOCUMENT} > First result displayed`
      const trackData = {
        eventAction,
        eventCategory: EVENT_CATEGORIES_BY_ROUTE_NAME[this.$route.name],
        eventLabel: 'Generate quiz from doc',
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
      }
      utils.sendCustomEventToGTM(eventAction, trackData)
    },
  },
}
</script>

<style>
.doc-to-quiz__result-modal {
  &.modal__hidden {
    transform: translate(0, -100%);
    transition: 0.2s ease-in-out;
  }
  .aha-modal__header {
    @apply bg-primary-50 absolute top-0 w-full z-1;
    .aha-modal__header--text {
      @apply mt-0;
    }
  }
  .aha-modal__body {
    @apply bg-primary-50 p-0 m-0 relative;
    height: unset;
    overflow-x: visible;
    .aha-modal__header--text {
      @apply mt-0;
    }
  }
  .doc-to-quiz__header {
    height: 56px;
  }
  .doc-to-quiz__wrapper {
    @apply rounded text-base m-0 overflow-y-auto py-0 px-6;
    height: 80vh;
    @media (max-width: 768px) {
      height: 70vh;
    }
  }
  .doc-to-quiz__bottom {
    height: 53px;
  }
  .content-item {
    @apply shadow rounded-lg my-4;
    @apply w-full;
    color: #000;
    background-color: #fff;
    &.is-first-content-item {
      margin-top: 0;
    }
    &::v-deep {
      ul {
        list-style: disc;
        padding-left: 1rem;
      }
      p:not(:last-child) {
        padding-bottom: 0.75rem;
      }
    }
  }
  .feedback-loading {
    @apply flex items-center justify-center;
    .feedback-loading__content {
      margin-top: 1.5rem;
    }
  }
}
</style>
